import "../styles/menu.css";
import { icon } from "../iconsKit";
import { isPaintedIcon, paintedIconHtml } from "../featureIcons";
import { sheetHeadHtml } from "../components";
import { uiUrl } from "../portraits";

/** One row as the sheet draws it: the hub builds these from the feature registry's rows and its own (Guide, Settings, Debug). */
export interface MoreRowView {
  /** The row's class (`.mrow.<id>`). */
  readonly id: string;
  /**
   * A kit icon name (ui/iconsKit.ts); a painted icon name (ui/featureIcons.ts); or a painted art file under
   * art/interface ("icons/shop_merchant.png") for a feature whose art ships as one large PNG; or inline `<svg>` markup. Feature rows are painted
   * (the UI style doc, rule O2); only the debug rows keep a stroke glyph.
   */
  readonly icon: string;
  readonly label: string;
  readonly note?: string;
  /** A red count on the row. */
  readonly dot?: number;
  onClick(): void;
}

/** A painted row icon's side: a touch larger than the 22 px stroke glyphs, which carry no ink line or shadow. */
const PAINTED_ROW_PX = 28;
/** A row icon given as an art file rather than a name. */
const ART_FILE = /\.(png|webp)$/;

function rowIcon(name: string): string {
  if (name.startsWith("<svg")) return name;                 // a feature's own inline glyph (Endless's infinity)
  if (isPaintedIcon(name)) return paintedIconHtml(name, PAINTED_ROW_PX);
  if (ART_FILE.test(name)) return `<img class="pico" decoding="sync" src="${uiUrl(name)}" width="${PAINTED_ROW_PX}" height="${PAINTED_ROW_PX}" alt="">`;
  return icon(name, 22);
}

/** Three dots: the kit has no "more" glyph. */
export const MORE_ICON = `<svg class="ico" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2.2"/><circle cx="12" cy="12" r="2.2"/><circle cx="19" cy="12" r="2.2"/></svg>`;

/** What a list sheet is called (M35: the ⚙ menu, the Events tab's page). */
export interface ListSheetHead {
  readonly title: string;
  readonly sub?: string;
  readonly portrait: string;
  readonly tint?: "navy" | "gold" | "ember" | "plum" | "wood" | "blue";
  /** Classes after `sheet panel more` (`menu`, `evhub`). */
  readonly cls: string;
  /**
   * UI pass 10-21: the ⚙ menu draws its painted feature rows as a grid of art tiles (red dot on the corner), and the
   * rows with only a stroke glyph (debug, the sandbox store) as compact rows under them. Off: one list of rows.
   */
  readonly grid?: boolean;
}

/** The ⚙ menu (M35 Option 1, replacing the More tab): Settings, the Guide, Achievements, the Almanac, the Market, debug. */
export const MENU_HEAD: ListSheetHead = { title: "Menu", portrait: paintedIconHtml("settings", 40), tint: "navy", cls: "menu", grid: true };

/** A menu tile's painted art (CSS px). */
const TILE_ART_PX = 54;

/** Whether a row is drawn as a tile: it has painted art (a feature), not a stroke glyph (debug and test rows). */
export function isTileRow(r: MoreRowView): boolean {
  if (/^debug\b/i.test(r.label)) return false;          // a test row with painted art (the skin switcher) stays a row
  return r.icon.startsWith("<svg") || isPaintedIcon(r.icon) || ART_FILE.test(r.icon);
}

function tileArt(name: string): string {
  if (name.startsWith("<svg")) return name;
  if (isPaintedIcon(name)) return paintedIconHtml(name, TILE_ART_PX);
  return `<img class="pico" decoding="sync" src="${uiUrl(name)}" width="${TILE_ART_PX}" height="${TILE_ART_PX}" alt="">`;
}

const dotHtml = (r: MoreRowView): string => ((r.dot ?? 0) > 0 ? `<span class="badge alert">${r.dot}</span>` : "");

/** A tile: the art on its plate, the name under it, the red count on the corner. No note: the art and dot say it. */
function tileHtml(r: MoreRowView): string {
  return `<button class="mrow ${r.id} mtile${(r.dot ?? 0) > 0 ? " has-dot" : ""}" aria-label="${r.label}"><span class="mtart">${tileArt(r.icon)}</span><b class="disp">${r.label}</b>${dotHtml(r)}</button>`;
}

/** A compact test row (debug, the sandbox store): the label over its note, both wrapping rather than cut off. */
function devRowHtml(r: MoreRowView): string {
  return `<button class="mrow ${r.id} mdev">${rowIcon(r.icon)}<span class="mdt"><b>${r.label}</b>${r.note ? `<small>${r.note}</small>` : ""}</span>${dotHtml(r)}${icon("chev", 14)}</button>`;
}

/** A list sheet: the rows in the order given (features/registry.ts MORE_ORDER), then the build's version. */
export function showMore(parent: HTMLElement, rows: readonly MoreRowView[], version: string, head: ListSheetHead = MENU_HEAD, before = ""): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = `sheet panel more ${head.cls}`;
  const row = (r: MoreRowView) => `<button class="mrow ${r.id}">${rowIcon(r.icon)}<b>${r.label}</b>${r.note ? `<small>${r.note}</small>` : ""}${(r.dot ?? 0) > 0 ? `<span class="badge alert">${r.dot}</span>` : ""}${icon("chev", 16)}</button>`;
  const tiles = head.grid ? rows.filter(isTileRow) : [], dev = head.grid ? rows.filter((r) => !isTileRow(r)) : [];
  const list = head.grid
    ? `<div class="mlist mgrid">
      ${tiles.map(tileHtml).join("\n      ")}
    </div>${dev.length > 0 ? `<div class="mlist mdevs">${dev.map(devRowHtml).join("")}</div>` : ""}`
    : `<div class="mlist">
      ${rows.map(row).join("\n      ")}
    </div>`;
  sheet.innerHTML = `
    ${sheetHeadHtml({ title: head.title, sub: head.sub, portrait: head.portrait, tint: head.tint ?? "navy" })}
    ${before}
    ${list}
    ${version ? `<small class="ver muted">${version}</small>` : ""}`;
  // Each row's sheet replaces this one (the hub's single-sheet rule), so the rows do not close it themselves.
  sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
  const order = head.grid ? [...tiles, ...dev] : rows;
  const buttons = sheet.querySelectorAll<HTMLButtonElement>(".mlist .mrow");
  order.forEach((r, i) => { buttons[i].onclick = () => r.onClick(); });
  parent.append(sheet);
  return sheet;
}
